import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Check, Minus, Plus, RotateCcw, User, Mail } from 'lucide-react'
import { cn } from '@/utils/misc'
import {
	CONTROL_TOKENS,
	DEFAULT_FONT_SIZES,
	FONT_SIZE_RANGES,
	useFontSizes,
	type FontSizeToken,
} from '@/contexts/font-size-context'

interface TokenRow {
	token: FontSizeToken
	label: string
	hint: string
}

const CALENDAR_TOKEN_ROWS: TokenRow[] = [
	{ token: 'toolbarTitle', label: 'Název měsíce', hint: '"Březen 2025" v horní liště' },
	{ token: 'dayDate', label: 'Datum dne', hint: 'Velké číslo data v záhlaví sloupce' },
	{ token: 'dayName', label: 'Den v týdnu', hint: 'Název dne (pondělí, úterý...)' },
	{ token: 'dayStats', label: 'Statistiky dne', hint: 'Počty pacientů a PP v záhlaví dne' },
	{ token: 'timeGutter', label: 'Časová osa', hint: 'Čas v levém sloupci kalendáře' },
	{ token: 'eventTitle', label: 'Název termínu', hint: 'Text v dlaždici termínu v kalendáři' },
	{ token: 'eventTime', label: 'Čas termínu', hint: 'Časový rozsah pod názvem termínu' },
	{ token: 'noteText', label: 'Text poznámky', hint: 'Text úkolu v řádku poznámek a tlačítka "Přidat"' },
	{ token: 'noteTime', label: 'Čas poznámky', hint: 'Časový štítek a počítadlo úkolů' },
]

const DIALOG_TOKEN_ROWS: TokenRow[] = [
	{ token: 'dialogTitle', label: 'Nadpis dialogu', hint: '"Nový termín" / "Upravit termín"' },
	{ token: 'dialogLabel', label: 'Popisek pole', hint: 'Štítky nad poli (Datum, Od, Do…)' },
	{ token: 'dialogInput', label: 'Text v poli', hint: 'Text uvnitř všech vstupů a výběrů' },
	{ token: 'dialogInputHeight', label: 'Výška polí', hint: 'Výška vstupních polí a výběrů' },
]

const CONTROL_TOKEN_SET = new Set<FontSizeToken>(CONTROL_TOKENS)

function isControlToken(token: FontSizeToken): boolean {
	return CONTROL_TOKEN_SET.has(token)
}

export function FontSizeSettingsPage() {
	const navigate = useNavigate()
	const { sizes, setSize, reset } = useFontSizes()

	const handleReset = useCallback(() => {
		if (window.confirm('Vrátit všechny velikosti na výchozí hodnoty?')) {
			reset()
		}
	}, [reset])

	return (
		<div className="min-h-screen bg-[#faf8f5]">
			<div className="mx-auto max-w-7xl px-6 py-6">
				{/* Header */}
				<div className="mb-6 flex items-center justify-between">
					<button
						type="button"
						onClick={() => navigate(-1)}
						className="inline-flex items-center gap-2 text-[#5c5142] hover:text-[#3d3428] transition-colors"
					>
						<ArrowLeft className="h-4 w-4" />
						<span>Zpět</span>
					</button>
					<h1 className="text-2xl font-semibold text-[#3d3428]">
						Velikost písma a prvků
					</h1>
					<button
						type="button"
						onClick={handleReset}
						className="inline-flex items-center gap-2 rounded-lg border border-[#ddd5c8] bg-white px-3 py-2 text-sm font-medium text-[#5c5142] hover:bg-[#f5ebe0] hover:border-[#d7ad6c] transition-colors"
					>
						<RotateCcw className="h-4 w-4" />
						Obnovit výchozí
					</button>
				</div>

				<p className="mb-6 text-sm text-[#6b5c4c]">
					Změny se ukládají automaticky a okamžitě se projeví v celé aplikaci.
				</p>

				<div className="grid grid-cols-1 lg:grid-cols-[minmax(0,440px)_minmax(0,1fr)] gap-6">
					{/* Controls column */}
					<div className="min-w-0">
						<SectionHeader>Kalendář</SectionHeader>
						{CALENDAR_TOKEN_ROWS.map((row) => (
							<TokenControl
								key={row.token}
								row={row}
								value={sizes[row.token]}
								onChange={(v) => setSize(row.token, v)}
							/>
						))}

						<SectionHeader>Dialog termínu</SectionHeader>
						{DIALOG_TOKEN_ROWS.map((row) => (
							<TokenControl
								key={row.token}
								row={row}
								value={sizes[row.token]}
								onChange={(v) => setSize(row.token, v)}
							/>
						))}
					</div>

					{/* Preview column */}
					<div className="min-w-0 lg:sticky lg:top-6 lg:self-start">
						<SectionHeader>Náhled</SectionHeader>
						<CalendarPreview />
						<div className="h-4" />
						<DialogPreview />
					</div>
				</div>
			</div>
		</div>
	)
}

function SectionHeader({ children }: { children: string }) {
	return (
		<h2 className="mb-3 mt-2 text-lg font-semibold text-[#3d3428]">
			{children}
		</h2>
	)
}

interface TokenControlProps {
	row: TokenRow
	value: number
	onChange: (value: number) => void
}

function TokenControl({ row, value, onChange }: TokenControlProps) {
	const { min, max } = FONT_SIZE_RANGES[row.token]
	const isDefault = value === DEFAULT_FONT_SIZES[row.token]
	const atMin = value <= min
	const atMax = value >= max
	const previewFontSize = isControlToken(row.token) ? 16 : value

	return (
		<div className="mb-3 rounded-xl border border-[#e8e4df] bg-white p-4">
			<div className="flex items-center justify-between gap-4">
				<div className="flex-1 min-w-0">
					<div className="font-medium text-[#3d3428]">{row.label}</div>
					<div className="mt-0.5 text-xs text-[#8c8275]">{row.hint}</div>
				</div>
				<div className="flex items-center gap-2 flex-shrink-0">
					<StepButton
						icon={<Minus className="h-4 w-4" />}
						disabled={atMin}
						onPress={() => onChange(value - 1)}
					/>
					<div className="min-w-[80px] text-center">
						<div
							className="font-semibold text-[#3d3428] leading-tight"
							style={{ fontSize: `${previewFontSize}px` }}
						>
							{value} px
						</div>
						{!isDefault && (
							<div className="text-[10px] text-[#8c8275]">
								výchozí: {DEFAULT_FONT_SIZES[row.token]}
							</div>
						)}
					</div>
					<StepButton
						icon={<Plus className="h-4 w-4" />}
						disabled={atMax}
						onPress={() => onChange(value + 1)}
					/>
				</div>
			</div>
		</div>
	)
}

interface StepButtonProps {
	icon: React.ReactNode
	disabled?: boolean
	onPress: () => void
}

function StepButton({ icon, disabled, onPress }: StepButtonProps) {
	return (
		<button
			type="button"
			onClick={onPress}
			disabled={disabled}
			className={cn(
				'inline-flex h-9 w-9 items-center justify-center rounded-lg border transition-colors',
				disabled
					? 'border-[#ebe7e2] bg-[#f8f6f3] text-[#c2b9ad] cursor-not-allowed'
					: 'border-[#ddd5c8] bg-white text-[#5c5142] hover:bg-[#f5ebe0] hover:border-[#d7ad6c]',
			)}
		>
			{icon}
		</button>
	)
}

interface MockDay {
	date: string
	day: string
	patients: number
	pp: number
	notes: { color?: string; time: string; text: string; done?: boolean }[]
}

const MOCK_DAYS: MockDay[] = [
	{
		date: '17.3.',
		day: 'pondělí',
		patients: 12,
		pp: 2,
		notes: [
			{ color: '#d7ad6c', time: '08:00', text: 'Objednat materiál', done: false },
			{ time: '12:00', text: 'Volat Novákové', done: true },
		],
	},
	{
		date: '18.3.',
		day: 'úterý',
		patients: 9,
		pp: 1,
		notes: [{ color: '#34a853', time: '09:30', text: 'Příprava ordinace', done: false }],
	},
	{
		date: '19.3.',
		day: 'středa',
		patients: 14,
		pp: 3,
		notes: [],
	},
]

const GUTTER_WIDTH = 48

function CalendarPreview() {
	return (
		<div className="rounded-xl border border-[#e8e4df] bg-white overflow-hidden">
			{/* Toolbar header */}
			<div className="px-4 py-3 border-b border-[#e8e4df] bg-[#f8f6f3]">
				<div
					className="font-semibold capitalize text-[#3d3428]"
					style={{ fontSize: 'var(--font-size-toolbar-title)' }}
				>
					Březen 2025
				</div>
				<div
					className="font-medium text-[#6b5c4c]"
					style={{ fontSize: 'calc(var(--font-size-toolbar-title) * 0.8)' }}
				>
					12. týden
				</div>
			</div>

			{/* Day headers row — left spacer aligns with time gutter below */}
			<div className="flex border-b border-[#e8e4df] bg-[#f8f6f3]">
				<div
					className="flex-shrink-0 border-r border-[#e8e4df] bg-[#f8f6f3]"
					style={{ width: GUTTER_WIDTH }}
				/>
				<div className="flex flex-1">
					{MOCK_DAYS.map((d, i) => (
						<div
							key={d.date}
							className={cn(
								'flex-1 min-w-0 flex flex-col items-center py-2',
								i > 0 && 'border-l border-[#e8e4df]',
							)}
						>
							<div
								className="font-semibold text-[#3d3428]"
								style={{ fontSize: 'var(--font-size-day-date)' }}
							>
								{d.date}
							</div>
							<div
								className="font-medium capitalize text-[#6b5c4c]"
								style={{ fontSize: 'var(--font-size-day-name)' }}
							>
								{d.day}
							</div>
							<div
								className="flex items-center gap-3 text-[#6b5c4c] mt-1"
								style={{ fontSize: 'var(--font-size-day-stats)' }}
							>
								<span className="inline-flex items-center gap-1">
									<User className="h-3.5 w-3.5" />
									<span className="font-semibold">{d.patients}</span>
								</span>
								<span className="inline-flex items-center gap-1">
									<span className="font-semibold">PP</span>
									<span className="font-semibold">{d.pp}</span>
								</span>
							</div>
						</div>
					))}
				</div>
			</div>

			{/* Notes row — same gutter spacer, then per-day note columns */}
			<div className="flex border-b border-[#e8e4df] bg-[#faf8f5]">
				<div
					className="flex-shrink-0 border-r border-[#e8e4df] bg-[#f8f6f3]"
					style={{ width: GUTTER_WIDTH }}
				/>
				<div className="flex flex-1">
					{MOCK_DAYS.map((d) => (
						<div
							key={d.date}
							className="flex-1 min-w-0 overflow-hidden border-r border-[#e8e4df] last:border-r-0 min-h-[60px] p-1 flex flex-col"
						>
							<div className="space-y-0.5 flex-1">
								{d.notes.map((n, idx) => (
									<MockNote key={idx} note={n} />
								))}
							</div>
							<div className="mt-1 flex items-center gap-1">
								<button
									type="button"
									className="flex-1 flex items-center justify-center gap-1 py-1 px-2 rounded text-[#8c8275]"
									style={{ fontSize: 'var(--font-size-note-text)' }}
									disabled
								>
									<Plus className="w-3 h-3" />
									<span>Přidat</span>
								</button>
								{d.notes.length > 0 && (
									<span
										className="text-[#8c8275] px-1"
										style={{ fontSize: 'var(--font-size-note-time)' }}
									>
										{d.notes.filter((n) => n.done).length}/{d.notes.length}
									</span>
								)}
							</div>
						</div>
					))}
				</div>
			</div>

			{/* Time grid: gutter on the left + 3 day columns with mock events */}
			<div className="flex">
				<div
					className="flex-shrink-0 bg-[#f8f6f3] border-r border-[#e8e4df]"
					style={{ width: GUTTER_WIDTH }}
				>
					{['08:00', '09:00', '10:00'].map((t) => (
						<div
							key={t}
							className="h-14 px-2 pt-1 text-[#8c8275]"
							style={{ fontSize: 'var(--font-size-time-gutter)' }}
						>
							{t}
						</div>
					))}
				</div>
				<div className="flex-1 grid grid-cols-3 relative" style={{ height: '168px' }}>
					{/* Day separators */}
					<div className="border-l border-[#ebe7e2]" />
					<div className="border-l border-[#ebe7e2]" />
					<div className="border-l border-[#ebe7e2]" />

					{/* Mock event chips */}
					<div
						className="absolute rounded-md bg-[#4285f4] text-white px-1.5 py-1 leading-tight overflow-hidden"
						style={{
							top: 6,
							left: '2%',
							width: '30%',
							height: 60,
							fontSize: 'var(--font-size-event-title)',
							boxShadow: 'inset 0 1px 0 0 white, 0 1px 3px rgba(0,0,0,0.1)',
						}}
					>
						<div className="font-medium truncate">Novák Jan</div>
						<div
							className="opacity-90"
							style={{ fontSize: 'var(--font-size-event-time)' }}
						>
							08:00 - 09:00
						</div>
					</div>
					<div
						className="absolute rounded-md bg-[#34a853] text-white px-1.5 py-1 leading-tight overflow-hidden"
						style={{
							top: 30,
							left: '36%',
							width: '30%',
							height: 28,
							fontSize: 'var(--font-size-event-title)',
							boxShadow: 'inset 0 1px 0 0 white, 0 1px 3px rgba(0,0,0,0.1)',
						}}
					>
						<span className="inline-flex items-center gap-1 truncate">
							<Mail className="h-3 w-3 flex-shrink-0" />
							Svobodová, 08:30
						</span>
					</div>
					<div
						className="absolute rounded-md bg-[#fbbc04] text-[#3d3428] px-1.5 py-1 leading-tight overflow-hidden"
						style={{
							top: 80,
							left: '70%',
							width: '28%',
							height: 80,
							fontSize: 'var(--font-size-event-title)',
							boxShadow: 'inset 0 1px 0 0 white, 0 1px 3px rgba(0,0,0,0.1)',
						}}
					>
						<div className="font-medium truncate">Dvořák Petr</div>
						<div
							className="opacity-80"
							style={{ fontSize: 'var(--font-size-event-time)' }}
						>
							09:00 - 10:30
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

function MockNote({ note }: { note: MockDay['notes'][number] }) {
	return (
		<div
			className={cn(
				'flex items-center gap-1.5 px-1.5 py-1 rounded-md min-w-0',
				note.done && 'opacity-60',
			)}
		>
			{note.color && (
				<span
					className="w-2.5 h-4 rounded-sm flex-shrink-0"
					style={{ backgroundColor: note.color }}
				/>
			)}
			<span
				className={cn(
					'flex-shrink-0 w-3.5 h-3.5 rounded border flex items-center justify-center',
					note.done
						? 'bg-[#d7ad6c] border-[#d7ad6c] text-white'
						: 'border-[#a09689]',
				)}
			>
				{note.done && <Check className="w-2.5 h-2.5" />}
			</span>
			<span
				className="flex-shrink-0 text-[#8c8275] bg-[#f0ede8] px-1 py-0.5 rounded"
				style={{ fontSize: 'var(--font-size-note-time)' }}
			>
				{note.time}
			</span>
			<span
				className={cn(
					'flex-1 min-w-0 text-[#3d3428] truncate',
					note.done && 'line-through text-[#8c8275]',
				)}
				style={{ fontSize: 'var(--font-size-note-text)' }}
			>
				{note.text}
			</span>
		</div>
	)
}

function DialogPreview() {
	return (
		<div className="rounded-xl border border-[#e8e4df] bg-white p-4 shadow-sm">
			<div className="flex items-center gap-3 pb-3 mb-3 border-b border-[#e8e4df]">
				<span className="flex items-center justify-center w-8 h-8 rounded-xl bg-[#f5ebe0]">
					<svg className="w-4 h-4 text-[#d7ad6c]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
						<rect x="3" y="4" width="18" height="18" rx="2" />
						<line x1="16" y1="2" x2="16" y2="6" />
						<line x1="8" y1="2" x2="8" y2="6" />
						<line x1="3" y1="10" x2="21" y2="10" />
					</svg>
				</span>
				<span style={{ fontSize: 'var(--font-size-dialog-title)' }} className="font-semibold text-[#3d3428]">
					Nový termín
				</span>
			</div>
			<div className="space-y-3">
				<div>
					<div
						className="text-[#5c5142] font-medium mb-1"
						style={{ fontSize: 'var(--font-size-dialog-label)' }}
					>
						Datum
					</div>
					<input
						readOnly
						value="2025-03-17"
						className="w-full rounded-xl border border-[#e8e4df] bg-[#faf8f5] px-3 text-[#3d3428] outline-none"
						style={{
							height: 'var(--input-height-dialog)',
							fontSize: 'var(--font-size-dialog-input)',
						}}
					/>
				</div>
				<div className="flex gap-3">
					<div className="flex-1">
						<div
							className="text-[#5c5142] font-medium mb-1"
							style={{ fontSize: 'var(--font-size-dialog-label)' }}
						>
							Od
						</div>
						<input
							readOnly
							value="08:00"
							className="w-full rounded-xl border border-[#e8e4df] bg-[#faf8f5] px-3 text-[#3d3428] outline-none"
							style={{
								height: 'var(--input-height-dialog)',
								fontSize: 'var(--font-size-dialog-input)',
							}}
						/>
					</div>
					<div className="flex-1">
						<div
							className="text-[#5c5142] font-medium mb-1"
							style={{ fontSize: 'var(--font-size-dialog-label)' }}
						>
							Do
						</div>
						<input
							readOnly
							value="09:00"
							className="w-full rounded-xl border border-[#e8e4df] bg-[#faf8f5] px-3 text-[#3d3428] outline-none"
							style={{
								height: 'var(--input-height-dialog)',
								fontSize: 'var(--font-size-dialog-input)',
							}}
						/>
					</div>
				</div>
				<div>
					<div
						className="text-[#5c5142] font-medium mb-1"
						style={{ fontSize: 'var(--font-size-dialog-label)' }}
					>
						Poznámka
					</div>
					<textarea
						readOnly
						rows={2}
						value="Ukázkový text poznámky."
						className="w-full rounded-xl border border-[#e8e4df] bg-[#faf8f5] px-3 py-2 text-[#3d3428] outline-none resize-none"
						style={{ fontSize: 'var(--font-size-dialog-input)' }}
					/>
				</div>
				<div className="flex justify-end gap-2 pt-2 border-t border-[#e8e4df]">
					<button
						type="button"
						className="h-10 px-4 rounded-xl border border-[#e8e4df] bg-white text-sm font-medium text-[#5c5142]"
						disabled
					>
						Zrušit
					</button>
					<button
						type="button"
						className="h-10 px-4 rounded-xl bg-[#d7ad6c] text-sm font-medium text-white"
						disabled
					>
						Uložit
					</button>
				</div>
			</div>
		</div>
	)
}

export default FontSizeSettingsPage
